import React, { useMemo, FC } from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import useTranslator from 'Client/hooks/useTranslator';
import BellaHybridLink from 'Client/components/Generic/Links/BellaHybridLink';
import ClubHybridLink from 'Club/components/Links/ClubHybridLink';
import { body2, header6 } from '@westwing/ui-kit/typography';
import phoneNumberToLink from 'Common/helpers/phoneNumber';
import useConfig from 'AppShell/hooks/useConfig';
import useOwwFooterTracking, {
    FOOTER_TRACKING_EVENT_CATEGORY,
} from 'OneWestwingFooter/providers/OwwFooterTrackingProvider';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { useAppBridge } from 'AppShell/appBridge/useAppBridge';
import useRouter from 'AppShell/hooks/useRouter';
import { trimTrailingSlash } from 'Client/urlService';

const FooterHelpInfoContainer = styled.div`
    background: ${({ theme }) => theme.colors.white};
    ${forDesktop`
        padding: 0px 40px;
    `}
`;

const FooterHelpInfoWrapper = styled.div`
    height: 144px;
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 1440px;
    margin: auto;
    flex-direction: column;
    justify-content: center;
    padding: 0px 20px;
    background: ${({ theme }) => theme.colors.charcoal10};
    ${forDesktop`
        background: ${({ theme }) => theme.colors.white};
        height: 68px;
        padding: 0px 40px;
        justify-content: normal;
        gap: 32px;
        flex-direction: row;
    `}
`;

const HelpInfoFooterLinks = styled.ul`
    display: flex;
    padding-inline-start: 0;
    gap: 24px;
    ${forDesktop`
       gap: 40px;
    `}
`;
const HelpInfoFooterLink = styled.li`
    position: relative;
    &:nth-child(2) {
        &:after {
            content: '';
            display: inline-block;
            width: 1px;
            top: 4px;
            height: 12px;
            background: ${({ theme }) => theme.colors.charcoal100};
            position: absolute;
            right: -12px;
            ${forDesktop`
               right: -20px;
            `}
        }
        &:before {
            content: '';
            display: inline-block;
            width: 1px;
            top: 4px;
            height: 12px;
            background: ${({ theme }) => theme.colors.charcoal100};
            left: -12px;
            position: absolute;
            ${forDesktop`
               left: -20px;
            `}
        }
    }
`;

const StyledBellaLink = styled(BellaHybridLink)`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const StyledClubLink = styled(ClubHybridLink)`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const HelpInfoTitle = styled.div`
    ${header6}
    font-size: 16px;
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const HelpInfoWorkingHours = styled.div`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal60};
    text-align: center;

    ${forDesktop`
        white-space: nowrap;
    `}
`;

interface FooterHelpInfoLink {
    text: string;
    trackingEvent: string;
    path: string;
    isExternal?: boolean;
    testId?: string;
}

export const FooterHelpInfo: FC<{ isClub?: boolean }> = ({ isClub = false }) => {
    const t = useTranslator();
    const config = useConfig();
    const { links } = config;
    const { ga } = useOwwFooterTracking();
    const { pageType } = useCurrentPageType();

    const { isAppWebviewPage } = useRouter();

    const track = (action: string) => {
        ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
            action,
            label: pageType,
        });
    };

    const appBridge = useAppBridge('mf-checkout-payment');

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, link: FooterHelpInfoLink) => {
        if (isAppWebviewPage && !link.isExternal) {
            e.preventDefault();
            appBridge.sendShowOverlay(`${trimTrailingSlash(links.url)}${link.path}`);
        }
        track(link.trackingEvent);
    };

    const { helpCenterContactUrl, helpCenterUrl } = isClub ? links.clubPaths : links;
    const helpLinks = useMemo<FooterHelpInfoLink[]>(
        () => [
            {
                text: 'Contact Form',
                trackingEvent: 'Contact Form - click',
                path: helpCenterContactUrl,
                testId: 'oww-footer-contact-form',
                isExternal: false,
            },
            {
                text: 'Footer_FAQ',
                trackingEvent: 'FAQ - click',
                path: helpCenterUrl,
                isExternal: false,
            },
            {
                text: 'Phone Number',
                trackingEvent: 'Phone Number - click',
                path: phoneNumberToLink(t('Phone number')),
                isExternal: true,
            },
        ],
        [helpCenterContactUrl, helpCenterUrl, t]
    );

    const Link = isClub ? StyledClubLink : StyledBellaLink;

    return (
        <FooterHelpInfoContainer>
            <FooterHelpInfoWrapper>
                <HelpInfoTitle>{t('Footer_do you need help')}</HelpInfoTitle>
                <HelpInfoFooterLinks aria-label={t('Footer_do you need help')}>
                    {helpLinks.map(link => (
                        <HelpInfoFooterLink key={link.path}>
                            <Link onClick={e => handleLinkClick(e, link)} to={link.path} testId={link.testId}>
                                {t(link.text)}
                            </Link>
                        </HelpInfoFooterLink>
                    ))}
                </HelpInfoFooterLinks>
                <HelpInfoWorkingHours>{t('Availability_hours').replace(/<br> /g, '')}</HelpInfoWorkingHours>
            </FooterHelpInfoWrapper>
        </FooterHelpInfoContainer>
    );
};
